import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Users, Plus, Loader2, Shield, UserCog, MoreHorizontal,
  Mail, Calendar, Power, PowerOff, KeyRound, Copy, Check
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

const AdminsList = () => {
  const { api, admin: currentAdmin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // New admin form
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'support'
  });

  // Password reset dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Role change dialog state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [changingRole, setChangingRole] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/api/super-admin/admins');
      setAdmins(res.data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!formData.username || !formData.email || !formData.full_name || !formData.password) {
      setError('All fields are required');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await api.post('/api/super-admin/auth/create-admin', formData);
      setShowAddDialog(false);
      setFormData({ username: '', email: '', full_name: '', password: '', role: 'support' });
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (adminId, currentStatus) => {
    try {
      await api.put(`/api/super-admin/admins/${adminId}`, {
        is_active: !currentStatus
      });
      fetchAdmins();
      toast.success(`Agent ${currentStatus ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      console.error('Failed to update admin:', err);
      toast.error(err.response?.data?.detail || 'Failed to update agent');
    }
  };

  const handleChangeRole = async () => {
    if (!roleChangeTarget || !newRole) return;
    
    setChangingRole(true);
    try {
      await api.put(`/api/super-admin/admins/${roleChangeTarget.id}`, {
        role: newRole
      });
      toast.success(`Role changed to ${newRole} successfully`);
      setShowRoleDialog(false);
      setRoleChangeTarget(null);
      setNewRole('');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(false);
    }
  };

  const openRoleDialog = (admin) => {
    setRoleChangeTarget(admin);
    setNewRole(admin.role);
    setShowRoleDialog(true);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    
    setResetting(true);
    try {
      const res = await api.post(`/api/super-admin/auth/reset-admin-password/${resetTarget.id}`);
      setResetResult(res.data);
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      setShowResetDialog(false);
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeResetDialog = () => {
    setShowResetDialog(false);
    setResetTarget(null);
    setResetResult(null);
    setCopied(false);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-[#ffd108] text-black">Admin</Badge>;
      case 'manager':
        return <Badge className="bg-blue-500 text-white">Manager</Badge>;
      default:
        return <Badge variant="secondary">Support</Badge>;
    }
  };

  const canManageAdmins = currentAdmin?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!canManageAdmins) {
    return (
      <div className="text-center py-12">
        <Shield className={`h-12 w-12 mx-auto ${isDark ? 'text-slate-500' : 'text-gray-400'} mb-4`} />
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Access Restricted</h2>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Only administrators can manage support agents.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Agents</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{admins.length} team member{admins.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
          <Plus className="mr-2 h-4 w-4" /> Add Agent
        </Button>
      </div>

      {/* Role Legend */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Badge className="bg-[#ffd108] text-black">Admin</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Full access, can manage agents</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500 text-white">Manager</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Can manage tenants & tickets</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Support</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Can view & respond to tickets</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Name</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Email</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Role</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Login</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id} className={isDark ? 'border-slate-700' : 'border-gray-100'}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${isDark ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                        <UserCog className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{admin.full_name}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>@{admin.username}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                      <Mail className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                      {admin.email}
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(admin.role)}</TableCell>
                  <TableCell>
                    {admin.is_active !== false ? (
                      <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {admin.last_login
                      ? formatDistanceToNow(new Date(admin.last_login), { addSuffix: true })
                      : 'Never'}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {admin.created_at ? format(new Date(admin.created_at), 'PP') : 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {admin.id !== currentAdmin?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openRoleDialog(admin)}
                          >
                            <Shield className="mr-2 h-4 w-4" /> Change Role
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setResetTarget(admin);
                              setShowResetDialog(true);
                            }}
                          >
                            <KeyRound className="mr-2 h-4 w-4" /> Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleActive(admin.id, admin.is_active !== false)}
                          >
                            {admin.is_active !== false ? (
                              <><PowerOff className="mr-2 h-4 w-4" /> Deactivate</>
                            ) : (
                              <><Power className="mr-2 h-4 w-4" /> Activate</>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Add Support Agent</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Create a new support agent account for the EQUIPR CORE admin portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="John Smith"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Username</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="jsmith"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@equipr.com"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support Agent</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {formData.role === 'admin' && 'Full access to all features including agent management'}
                {formData.role === 'manager' && 'Can manage tenants and handle support tickets'}
                {formData.role === 'support' && 'Can view tenants and respond to support tickets'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddAdmin}
              disabled={submitting}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create Agent'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={closeResetDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              {resetResult ? 'Password Reset Complete' : 'Reset Password'}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              {resetResult 
                ? `A temporary password has been generated for ${resetResult.admin_name}.`
                : `Generate a temporary password for ${resetTarget?.full_name}. They will be required to change it on their next login.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetResult ? (
            <div className="py-4 space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className={`flex-1 text-lg font-mono ${isDark ? 'text-[#ffd108]' : 'text-amber-600'}`}>
                    {resetResult.temporary_password}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(resetResult.temporary_password)}
                    className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                  >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>Share this password securely with the user. They will be prompted to create a new password when they log in.</p>
              </div>
            </div>
          ) : (
            <div className="py-4">
              <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
                <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>This will generate a new temporary password and invalidate the current password. The user will need to change their password on next login.</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {resetResult ? (
              <Button onClick={closeResetDialog} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                Done
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={closeResetDialog}>Cancel</Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resetting}
                  className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                >
                  {resetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              Change Role
            </DialogTitle>
            <DialogDescription>
              Change the role for {roleChangeTarget?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Current Role</Label>
              <div className="flex items-center gap-2">
                {getRoleBadge(roleChangeTarget?.role)}
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  ({roleChangeTarget?.email})
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support - Basic ticket handling</SelectItem>
                  <SelectItem value="manager">Manager - Can assign tickets</SelectItem>
                  <SelectItem value="admin">Admin - Full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              <p><strong>Support:</strong> Can view and respond to tickets</p>
              <p><strong>Manager:</strong> Can assign tickets and view reports</p>
              <p><strong>Admin:</strong> Full access including user management</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowRoleDialog(false);
                setRoleChangeTarget(null);
                setNewRole('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeRole}
              disabled={changingRole || newRole === roleChangeTarget?.role}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              {changingRole ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Change Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminsList;
